import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { BaseButton, BaseTextField } from '../../base';
import BaseModal from '../../base/BaseModal';
import BackupServiceApi, { BackupInfo, BackupSettings } from '../../../../services/BackupService';

// ── Design tokens ─────────────────────────────────────────────────────────────
const FONT = '"Manrope", sans-serif';
const ACCENT = '#005e5d';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const BORDER = '#E6E7EB';
const SURFACE = '#F2F3F5';

const headerCellSx = {
  fontFamily: FONT, fontWeight: 700, fontSize: '0.75rem',
  letterSpacing: '0.05em', textTransform: 'uppercase' as const,
  color: TEXT_SECONDARY, backgroundColor: SURFACE,
  borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2,
};
const bodyCellSx = {
  fontFamily: FONT, fontSize: '0.875rem', color: TEXT_PRIMARY,
  borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ── Component ─────────────────────────────────────────────────────────────────
const BackupManagementPage: React.FC = () => {
  const [backups, setBackups]       = useState<BackupInfo[]>([]);
  const [settings, setSettings]     = useState<BackupSettings | null>(null);
  const [loading, setLoading]       = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);

  // Upload
  const fileInputRef                          = useRef<HTMLInputElement>(null);
  const [uploading, setUploading]             = useState(false);
  const [uploadProgress, setUploadProgress]   = useState(0);

  // Settings modal
  const [settingsOpen, setSettingsOpen]       = useState(false);
  const [localSettings, setLocalSettings]     = useState<BackupSettings | null>(null);
  const [savingSettings, setSavingSettings]   = useState(false);

  // Create backup modal
  const [createOpen, setCreateOpen]   = useState(false);
  const [newLabel, setNewLabel]       = useState('');
  const [creating, setCreating]       = useState(false);

  // Confirm dialogs
  const [confirmRestore, setConfirmRestore] = useState<BackupInfo | null>(null);
  const [confirmDelete, setConfirmDelete]   = useState<BackupInfo | null>(null);

  // ── Data loading ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bkps, cfg] = await Promise.all([
        BackupServiceApi.listBackups(),
        BackupServiceApi.getSettings(),
      ]);
      setBackups(bkps);
      setSettings(cfg);
    } catch (e: any) {
      setError(e?.message || 'Failed to load backup data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(null); setSuccess(null); }, 4000);
  };

  // ── Create backup ─────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    try {
      await BackupServiceApi.createBackup(newLabel.trim() || undefined);
      setCreateOpen(false);
      setNewLabel('');
      flash('Backup created successfully');
      await load();
    } catch (e: any) {
      flash(e?.message || 'Failed to create backup', true);
    } finally {
      setCreating(false);
    }
  };

  // ── Restore ───────────────────────────────────────────────────────────────
  const handleRestore = async (backup: BackupInfo) => {
    setConfirmRestore(null);
    setActionLoading(`restore_${backup.filename}`);
    try {
      await BackupServiceApi.restoreBackup(backup.filename);
      flash('Restore completed successfully');
    } catch (e: any) {
      flash(e?.message || 'Restore failed', true);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (backup: BackupInfo) => {
    setConfirmDelete(null);
    setActionLoading(`delete_${backup.filename}`);
    try {
      await BackupServiceApi.deleteBackup(backup.filename);
      flash('Backup deleted');
      setBackups(prev => prev.filter(b => b.filename !== backup.filename));
    } catch (e: any) {
      flash(e?.message || 'Delete failed', true);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected next time
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      flash('Only ZIP backup files are supported', true);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const info = await BackupServiceApi.uploadBackup(file, setUploadProgress);
      flash(`Backup "${info.filename}" uploaded successfully`);
      await load();
    } catch (err: any) {
      flash(err?.message || 'Upload failed', true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ── Download ────────────────────────────────────────────────────────────
  const handleDownload = async (backup: BackupInfo) => {
    try {
      const url = BackupServiceApi.getDownloadUrl(backup.filename);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = backup.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      flash(e?.message || 'Download failed', true);
    }
  };

  // ── Settings ──────────────────────────────────────────────────────────────
  const openSettings = () => {
    setLocalSettings(settings ? { ...settings } : null);
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    if (!localSettings) return;
    setSavingSettings(true);
    try {
      const updated = await BackupServiceApi.updateSettings(localSettings);
      setSettings(updated);
      setSettingsOpen(false);
      flash('Settings saved');
    } catch (e: any) {
      flash(e?.message || 'Failed to save settings', true);
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, fontFamily: FONT }}>
      {/* Hidden file input for backup upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
            Backup Management
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_TERTIARY, mt: 0.5, fontFamily: FONT }}>
            Create, restore, and manage database backups
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <BaseButton variant="outlined" colorVariant="secondary" onClick={openSettings} startIcon={<SettingsIcon />}>
            Schedule Settings
          </BaseButton>
          <BaseButton variant="outlined" colorVariant="secondary" onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
            Refresh
          </BaseButton>
          <BaseButton variant="outlined" colorVariant="secondary" onClick={handleUploadClick} startIcon={<UploadIcon />} disabled={uploading}>
            {uploading ? `Uploading ${uploadProgress}%` : 'Upload Backup'}
          </BaseButton>
          <BaseButton variant="contained" colorVariant="primary" onClick={() => setCreateOpen(true)} startIcon={<AddIcon />}>
            Create Backup
          </BaseButton>
        </Box>
      </Box>

      {/* Upload progress bar */}
      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_SECONDARY, mb: 0.5 }}>
            Uploading backup… {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 1, height: 6 }} />
        </Box>
      )}

      {/* Feedback */}
      {error   && <Alert severity="error"   sx={{ mb: 2, fontFamily: FONT }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontFamily: FONT }}>{success}</Alert>}

      {/* Schedule status card */}
      {settings && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ScheduleIcon sx={{ color: ACCENT }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.875rem', color: TEXT_PRIMARY }}>
              Auto Backup: <Chip label={settings.enabled ? 'Enabled' : 'Disabled'}
                size="small"
                color={settings.enabled ? 'success' : 'default'}
                sx={{ fontFamily: FONT, fontWeight: 700, ml: 0.5 }} />
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_SECONDARY, mt: 0.25 }}>
              Schedule: <code style={{ background: SURFACE, padding: '1px 6px', borderRadius: 4 }}>{settings.schedule}</code>
              {' '}· Keep last <strong>{settings.retainCount || '∞'}</strong> backups
            </Typography>
          </Box>
          {settings.lastRunAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {settings.lastRunStatus === 'ok'
                ? <CheckCircleIcon sx={{ fontSize: 16, color: '#2E7D32' }} />
                : <ErrorIcon sx={{ fontSize: 16, color: '#C62828' }} />}
              <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_TERTIARY }}>
                Last run: {formatDate(settings.lastRunAt)}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Backup table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Filename</TableCell>
                <TableCell sx={headerCellSx}>Label</TableCell>
                <TableCell sx={headerCellSx}>Created</TableCell>
                <TableCell sx={headerCellSx}>Size</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ ...bodyCellSx, textAlign: 'center', py: 6, color: TEXT_TERTIARY }}>
                    No backups yet. Click "Create Backup" to get started.
                  </TableCell>
                </TableRow>
              ) : backups.map(backup => {
                const isActing = actionLoading === `restore_${backup.filename}` || actionLoading === `delete_${backup.filename}`;
                return (
                  <TableRow key={backup.filename} hover sx={{ '&:hover': { bgcolor: `${ACCENT}06` } }}>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: TEXT_PRIMARY }}>
                        {backup.filename}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_SECONDARY }}>
                        {backup.label || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{formatDate(backup.createdAt)}</TableCell>
                    <TableCell sx={bodyCellSx}>{formatBytes(backup.size)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                      {isActing ? (
                        <CircularProgress size={20} sx={{ color: ACCENT }} />
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(backup)}
                              sx={{ color: '#1565C0', '&:hover': { bgcolor: '#E3F2FD' } }}>
                              <DownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore (will overwrite current data)">
                            <IconButton size="small" onClick={() => setConfirmRestore(backup)}
                              sx={{ color: '#E65100', '&:hover': { bgcolor: '#FFF3E0' } }}>
                              <RestoreIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setConfirmDelete(backup)}
                              sx={{ color: '#C62828', '&:hover': { bgcolor: '#FFEBEE' } }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Create Backup Modal ─────────────────────────────────────────────── */}
      <BaseModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setNewLabel(''); }}
        title="Create New Backup"
        maxWidth="sm"
        onSubmit={handleCreate}
        isLoading={creating}
        submitButtonText="Create Backup"
      >
        {creating && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: TEXT_SECONDARY, mb: 2 }}>
          This will create a ZIP archive containing a full MongoDB dump and all media files. This may take a few minutes for large databases.
        </Typography>
        <BaseTextField
          label="Label (optional)"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder="e.g., pre-deployment backup"
          helperText="A short description to identify this backup."
          disabled={creating}
        />
      </BaseModal>

      {/* ── Settings Modal ──────────────────────────────────────────────────── */}
      <BaseModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Auto Backup Settings"
        maxWidth="sm"
        onSubmit={saveSettings}
        isLoading={savingSettings}
        submitButtonText="Save Settings"
      >
        {localSettings && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.enabled}
                  onChange={e => setLocalSettings({ ...localSettings, enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT } }}
                />
              }
              label={<Typography sx={{ fontFamily: FONT, fontWeight: 600, color: TEXT_PRIMARY }}>Enable automatic backups</Typography>}
            />

            <Divider />

            <BaseTextField
              label="Cron Schedule"
              value={localSettings.schedule}
              onChange={e => setLocalSettings({ ...localSettings, schedule: e.target.value })}
              placeholder="0 2 * * 0"
              helperText='Standard cron format. Example: "0 2 * * 0" = every Sunday at 2am. Uses Asia/Ho_Chi_Minh timezone.'
            />

            <BaseTextField
              label="Retain Last N Backups"
              type="number"
              value={String(localSettings.retainCount)}
              onChange={e => setLocalSettings({ ...localSettings, retainCount: Math.max(0, parseInt(e.target.value) || 0) })}
              helperText="Older backups are deleted automatically. Set to 0 to keep all."
            />

            {localSettings.lastRunAt && (
              <Box sx={{ p: 1.5, bgcolor: SURFACE, borderRadius: '8px' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5 }}>Last Automatic Run</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_PRIMARY }}>
                  {formatDate(localSettings.lastRunAt)}
                  {' '}&mdash;{' '}
                  {localSettings.lastRunStatus === 'ok'
                    ? <span style={{ color: '#2E7D32', fontWeight: 700 }}>Success</span>
                    : <span style={{ color: '#C62828', fontWeight: 700 }}>{localSettings.lastRunError || 'Error'}</span>}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </BaseModal>

      {/* ── Confirm Restore Dialog ──────────────────────────────────────────── */}
      <Dialog open={!!confirmRestore} onClose={() => setConfirmRestore(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Restore Backup</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT }}>
            <strong>Warning:</strong> This will permanently overwrite all current data (database + media files) with the contents of:
            <br /><br />
            <code style={{ background: SURFACE, padding: '4px 8px', borderRadius: 4, fontSize: '12px' }}>
              {confirmRestore?.filename}
            </code>
            <br /><br />
            This action cannot be undone. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <BaseButton variant="outlined" colorVariant="secondary" onClick={() => setConfirmRestore(null)}>Cancel</BaseButton>
          <BaseButton variant="contained" onClick={() => confirmRestore && handleRestore(confirmRestore)}>
            Yes, Restore
          </BaseButton>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete Dialog ───────────────────────────────────────────── */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Delete Backup</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT }}>
            Delete <code style={{ background: SURFACE, padding: '2px 6px', borderRadius: 4, fontSize: '12px' }}>{confirmDelete?.filename}</code>?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <BaseButton variant="outlined" colorVariant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</BaseButton>
          <BaseButton variant="contained"  onClick={() => confirmDelete && handleDelete(confirmDelete)}>
            Delete
          </BaseButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackupManagementPage;
